import axios from "axios";
import { BASE_URL } from "./url_config.js";
import { log } from "@/composables/useLog";

/**
 * Get list of active users from a subclient for impersonation selection
 * @param {string} token - Auth token
 * @param {number} clientId - Target client ID
 * @returns {Promise<{error: boolean, client_name?: string, users?: Array, message?: string}>}
 */
export const getClientUsersForImpersonation = async (token, clientId) => {
  log("getClientUsersForImpersonation for client: " + clientId);

  try {
    const headers = {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Accept": "application/json"
    };

    const response = await axios.get(
      `${BASE_URL}/home/get_client_users_for_impersonation_api`,
      {
        headers,
        params: { client_id: clientId }
      }
    );

    log("getClientUsersForImpersonation result:", response.data);
    return response.data;
  } catch (err) {
    log("Error in getClientUsersForImpersonation:", err);
    return { error: true, message: err.message || "Failed to get users" };
  }
};

/**
 * Generate an impersonation token for a target user
 * @param {string} token - Auth token
 * @param {number} targetUserId - ID of user to impersonate
 * @returns {Promise<{error: boolean, token?: string, target_user_name?: string, target_client_name?: string, expires_in?: number, message?: string}>}
 */
export const generateImpersonationToken = async (token, targetUserId) => {
  log("generateImpersonationToken for user: " + targetUserId);

  try {
    const headers = {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Accept": "application/json"
    };

    const response = await axios.post(
      `${BASE_URL}/home/generate_impersonation_token_api`,
      { target_user_id: targetUserId },
      { headers }
    );

    log("generateImpersonationToken result:", response.data);
    return response.data;
  } catch (err) {
    log("Error in generateImpersonationToken:", err);
    return { error: true, message: err.message || "Failed to generate token" };
  }
};

/**
 * Login with an impersonation token (no prior auth required)
 * @param {string} impersonationToken - The one-time impersonation token
 * @returns {Promise<{error: boolean, token?: string, user?: object, loggedIn?: boolean, message?: string}>}
 */
export const loginWithImpersonationToken = async (impersonationToken) => {
  log("loginWithImpersonationToken");

  try {
    const response = await axios.post(
      `${BASE_URL}/home/login_with_impersonation_token_api`,
      { impersonation_token: impersonationToken }
    );

    log("loginWithImpersonationToken result:", response.data);
    return response.data;
  } catch (err) {
    log("Error in loginWithImpersonationToken:", err);
    return { error: true, message: err.message || "Failed to login with token" };
  }
};

/**
 * Build the impersonation login URL
 * @param {string} token - The impersonation token
 * @returns {string} - Full URL for impersonation login
 */
export const getImpersonationLoginUrl = (token) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/impersonate?token=${encodeURIComponent(token)}`;
};

/**
 * Open impersonation in new tab
 * @param {string} token - The impersonation token
 */
export const openImpersonationTab = (token) => {
  const url = getImpersonationLoginUrl(token);
  window.open(url, '_blank');
};

const useImpersonation = () => {
  return {
    getClientUsersForImpersonation,
    generateImpersonationToken,
    loginWithImpersonationToken,
    getImpersonationLoginUrl,
    openImpersonationTab
  };
};

export default useImpersonation;
